import React from "react";
import {
	ResponsiveContainer,
	LineChart,
	Line,
	BarChart,
	Bar,
	AreaChart,
	Area,
	PieChart,
	Pie,
	Cell,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
} from "recharts";
import { useTheme } from "../contexts/ThemeContext";

const PALETTE = [
	"#6366f1",
	"#22c55e",
	"#f59e0b",
	"#ef4444",
	"#06b6d4",
	"#a855f7",
	"#ec4899",
	"#84cc16",
];

/**
 * Renders a ```chart fenced block emitted by the AI analyst.
 * Spec: {type: 'line'|'bar'|'area'|'pie', title, xKey, data: [...], series: [{dataKey, name}]}
 * Invalid/partial JSON (mid-stream) renders a neutral placeholder instead of crashing.
 *
 * NOTE: Recharts discovers XAxis/YAxis/Tooltip/etc. by inspecting DIRECT
 * children — wrapping them in a <>fragment</> hides them and silently drops
 * axes and tooltips. They must be inlined per chart type.
 */
const ChartBlock = React.memo(({ source }) => {
	const { isDarkMode } = useTheme();

	let spec = null;
	try {
		spec = JSON.parse(source);
	} catch {
		return <div className="ai-chart-placeholder">Rendering chart…</div>;
	}
	if (
		!spec ||
		!Array.isArray(spec.data) ||
		spec.data.length === 0 ||
		!Array.isArray(spec.series) ||
		spec.series.length === 0
	) {
		return <div className="ai-chart-placeholder">Chart data unavailable</div>;
	}

	const gridColor = isDarkMode ? "#262626" : "#e4e4e7";
	const tickColor = isDarkMode ? "#a1a1aa" : "#71717a";
	const tooltipStyle = {
		backgroundColor: isDarkMode ? "#171717" : "#ffffff",
		border: `1px solid ${gridColor}`,
		borderRadius: 8,
		color: isDarkMode ? "#fafafa" : "#09090b",
		fontSize: 12.5,
	};
	const margin = { top: 8, right: 12, bottom: 4, left: 0 };
	const xTick = { fill: tickColor, fontSize: 11 };
	const yTick = { fill: tickColor, fontSize: 11 };

	let chart;
	if (spec.type === "bar") {
		chart = (
			<BarChart data={spec.data} margin={margin}>
				<CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
				<XAxis dataKey={spec.xKey} tick={xTick} stroke={gridColor} />
				<YAxis tick={yTick} stroke={gridColor} width={44} />
				<Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(148,163,184,0.08)" }} />
				<Legend wrapperStyle={{ fontSize: 12 }} />
				{spec.series.map((s, i) => (
					<Bar
						key={s.dataKey}
						dataKey={s.dataKey}
						name={s.name || s.dataKey}
						fill={PALETTE[i % PALETTE.length]}
						radius={[4, 4, 0, 0]}
					/>
				))}
			</BarChart>
		);
	} else if (spec.type === "area") {
		chart = (
			<AreaChart data={spec.data} margin={margin}>
				<CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
				<XAxis dataKey={spec.xKey} tick={xTick} stroke={gridColor} />
				<YAxis tick={yTick} stroke={gridColor} width={44} />
				<Tooltip contentStyle={tooltipStyle} />
				<Legend wrapperStyle={{ fontSize: 12 }} />
				{spec.series.map((s, i) => (
					<Area
						key={s.dataKey}
						type="monotone"
						dataKey={s.dataKey}
						name={s.name || s.dataKey}
						stroke={PALETTE[i % PALETTE.length]}
						fill={PALETTE[i % PALETTE.length]}
						fillOpacity={0.2}
					/>
				))}
			</AreaChart>
		);
	} else if (spec.type === "pie") {
		const valueKey = spec.series[0].dataKey;
		chart = (
			<PieChart>
				<Tooltip contentStyle={tooltipStyle} />
				<Legend wrapperStyle={{ fontSize: 12 }} />
				<Pie
					data={spec.data}
					dataKey={valueKey}
					nameKey={spec.xKey}
					outerRadius={100}
					label
				>
					{spec.data.map((_, i) => (
						<Cell key={i} fill={PALETTE[i % PALETTE.length]} />
					))}
				</Pie>
			</PieChart>
		);
	} else {
		// default: line
		chart = (
			<LineChart data={spec.data} margin={margin}>
				<CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
				<XAxis dataKey={spec.xKey} tick={xTick} stroke={gridColor} />
				<YAxis tick={yTick} stroke={gridColor} width={44} />
				<Tooltip contentStyle={tooltipStyle} />
				<Legend wrapperStyle={{ fontSize: 12 }} />
				{spec.series.map((s, i) => (
					<Line
						key={s.dataKey}
						type="monotone"
						dataKey={s.dataKey}
						name={s.name || s.dataKey}
						stroke={PALETTE[i % PALETTE.length]}
						strokeWidth={2}
						dot={spec.data.length <= 31}
					/>
				))}
			</LineChart>
		);
	}

	return (
		<div className="ai-chart-block">
			{spec.title && <div className="ai-chart-title">{spec.title}</div>}
			<ResponsiveContainer width="100%" height={280}>
				{chart}
			</ResponsiveContainer>
		</div>
	);
});
// React.memo: `source` is a stable string per message, so committed charts
// don't re-render (and Recharts doesn't replay its animation) while the next
// answer streams in. Theme changes still propagate via context.

export default ChartBlock;
